import React, { useState, useRef, useEffect } from 'react'
import { VPSvg, VUSvg } from './VehicleMapSchematic'
import { PhotoSlot } from '../utils/constants'
import { Camera } from 'lucide-react'

type EditorProps = {
  slots: PhotoSlot[]
  onSaveSlot: (id: string, patch: Partial<PhotoSlot>) => Promise<void>
}

export function SchemaPositionEditor({ slots, onSaveSlot }: EditorProps) {
  const [activeTab, setActiveTab] = useState<'VP' | 'VU'>('VP')

  return (
    <div className="flex flex-col gap-6 bg-gray-50 p-4 rounded-xl border border-gray-200">
      <div className="flex justify-between items-center">
        <h3 className="font-bold text-gray-800">Éditeur de Schéma</h3>
        <div className="flex bg-gray-200 p-1 rounded-lg">
          <button
            onClick={() => setActiveTab('VP')}
            className={`px-4 py-1 text-sm font-semibold rounded-md transition ${activeTab === 'VP' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            VP (Berline)
          </button>
          <button
            onClick={() => setActiveTab('VU')}
            className={`px-4 py-1 text-sm font-semibold rounded-md transition ${activeTab === 'VU' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            VU (Fourgon)
          </button>
        </div>
      </div>

      <p className="text-sm text-gray-600">
        Glissez et déposez les icônes sur le schéma pour ajuster leur position. Les modifications sont enregistrées automatiquement.
      </p>

      {/* Container for schematic */}
      <div className="relative bg-[#0b1121] rounded-2xl overflow-hidden aspect-[3/4.5] w-full max-w-sm mx-auto shadow-inner border border-gray-700 select-none">
        <div className="absolute inset-0 p-8 pointer-events-none flex items-center justify-center">
          {activeTab === 'VP' ? <VPSvg /> : <VUSvg />}
        </div>
        
        {slots.map(slot => (
          <DraggableSlot
            key={slot.id}
            slot={slot}
            type={activeTab}
            onDrop={(x, y) => {
              if (activeTab === 'VP') onSaveSlot(slot.id, { vp_x: x, vp_y: y })
              else onSaveSlot(slot.id, { vu_x: x, vu_y: y })
            }}
          />
        ))}
      </div>
    </div>
  )
}

function DraggableSlot({ slot, type, onDrop }: { slot: PhotoSlot, type: 'VP' | 'VU', onDrop: (x: number, y: number) => void }) {
  // If coordinates are missing, put them in the center (50, 50)
  const initialX = (type === 'VP' ? slot.vp_x : slot.vu_x) ?? 50
  const initialY = (type === 'VP' ? slot.vp_y : slot.vu_y) ?? 50
  
  const [localPos, setLocalPos] = useState({ x: initialX, y: initialY })
  const [isDragging, setIsDragging] = useState(false)
  const parentRef = useRef<HTMLElement | null>(null)

  const handlePointerDown = (e: React.PointerEvent) => {
    setIsDragging(true)
    parentRef.current = (e.currentTarget as HTMLElement).parentElement
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  }

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging || !parentRef.current) return
    const rect = parentRef.current.getBoundingClientRect()
    let x = ((e.clientX - rect.left) / rect.width) * 100
    let y = ((e.clientY - rect.top) / rect.height) * 100
    x = Math.max(0, Math.min(100, x))
    y = Math.max(0, Math.min(100, y))
    setLocalPos({ x, y })
  }

  const handlePointerUp = (e: React.PointerEvent) => {
    setIsDragging(false)
    ;(e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId)
    // Only trigger onDrop if position actually changed
    if (Math.round(localPos.x) !== initialX || Math.round(localPos.y) !== initialY) {
      onDrop(Math.round(localPos.x), Math.round(localPos.y))
    }
  }

  // Effect to update localPos if props change externally
  useEffect(() => {
    if (!isDragging) {
      setLocalPos({ x: initialX, y: initialY })
    }
  }, [initialX, initialY, isDragging])

  return (
    <div
      className={`absolute flex flex-col items-center gap-1 cursor-grab touch-none -translate-x-1/2 -translate-y-1/2 ${isDragging ? 'scale-110 cursor-grabbing z-50' : 'z-10 hover:z-20'}`}
      style={{ left: `${localPos.x}%`, top: `${localPos.y}%` }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      <div className="w-11 h-11 rounded-full bg-slate-800 border-2 border-white flex items-center justify-center shadow-[0_4px_12px_rgba(0,0,0,0.6)]">
        <Camera size={20} className="text-amber-400" />
      </div>
      <span className="text-[10px] text-amber-300 font-bold text-center leading-tight whitespace-nowrap bg-black/60 px-1.5 py-0.5 rounded backdrop-blur-sm border border-white/10 select-none">
        {slot.label}
      </span>
    </div>
  )
}
